import styled from '@emotion/styled'
import {graphql} from 'gatsby'
import React from 'react'
import {ArticleCard} from '../../components/blog/ArticleCard'
import {CATEGORIES_MAP, Category, CATEGORY_SIZES} from '../../components/blog/Categories'
import {CategoryCard} from '../../components/blog/CategoryCard'
import {BlogFooter} from '../../components/blog/Footer'
import {Lens} from '../../components/blog/Lens'
import {Slider} from '../../components/blog/Slider'
import {Body} from "../../components/Body";
import {Section} from "../../components/content";
import {SiteLink} from '../../components/SiteLink'
import {Colors} from '../../styles/colors'
import {Spacing} from '../../styles/spacing'
import {Typography} from '../../styles/typography'
import {cssOnlyDesktop, cssOnlyMobile, isDesktop, MOBILE_MAX_WIDTH} from '../../styles/utils'
import {blogName, CATEGORY_ENUM} from '../../utils/blog/const'
import {extractCategory, extractLens, urlForNode} from '../../utils/blog/utils'
import {PageBreak} from "../../components/blog/PageBreak";
import {BlogHeader} from "../../components/blog/Header";
import Seo from "../../components/Seo";

const TitleBox = styled.div`
  width: 696px;
  margin-bottom: 16px;
  ${Typography.heading.h2_700};
  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    width: auto;
    ${Typography.heading.h4_700}
  }
`
const LinkButton = styled.div`
  border-radius: 40px;
  padding: 8px 16px;
  border: 1px solid #D2D2D2;
  box-sizing: border-box;
  display: inline-block;
  ${Typography.label.label2_400}
`

const BlogHomepage = ({data, pageContext}) => {
  const {
    recentsPosts: {nodes: [lastArticle, ...recenti] = []} = {},
    mostViewed: {nodes: mostViewed = []} = {},
    luceGasPosts: {nodes: luceGas = []} = {},
    mobilitaElettricaPosts: {nodes: eMobility = []} = {},
    fotovoltaicoPosts: {nodes: fotovoltaico = []} = {}
  } = data
  const lastArticleCategory = extractCategory(lastArticle.tags)
  const lastArticleLens = extractLens(lastArticle.tags)

  mostViewed.sort(function(postA, postB){
    return pageContext.mostViewed.indexOf(postA.uuid) - pageContext.mostViewed.indexOf(postB.uuid);
  });

  return (
    <Body safe={false} HeaderComponent={BlogHeader} FooterComponent={BlogFooter}>
      <Seo title={'Elettrica'}/>
      <Section internalStyle={isDesktop() ? {} : {paddingTop: '20px'}}>
        {/*Hero*/}
        <div css={[Spacing.Flex.row, Spacing.Margin.bottom_16]}>
          <Lens type={lastArticleLens.ghostSlug}/>
          <div css={[Colors.Color.eerieBlack800, Spacing.Margin.horizontal_8]}>•</div>
          <Category category={lastArticleCategory.ghostSlug} css={[Colors.Color.eerieBlack800]}/>
        </div>
        <TitleBox>{lastArticle.title}</TitleBox>
        <SiteLink to={urlForNode(lastArticle)} name={lastArticle.title}>
          <LinkButton>Leggi l’articolo</LinkButton>
        </SiteLink>

        {/*Recenti*/}
        <div
          css={[Typography.heading.h5_700, cssOnlyDesktop(Spacing.Margin.top_80), cssOnlyMobile(Spacing.Margin.top_48), Spacing.Margin.bottom_20]}>Recenti
        </div>
        <Slider>
          {recenti.map((art, idx) => <ArticleCard key={idx} article={art}/>)}
        </Slider>
        {/*Le piu' lette*/}
        <div css={[Typography.heading.h5_700, Spacing.Margin.top_48, Spacing.Margin.bottom_20]}>Le più lette</div>
        <Slider cardSize={'large'}>
          {mostViewed.map((art, idx) => <ArticleCard key={idx} number={idx + 1} article={art}/>)}
        </Slider>

        <PageBreak/>

        {/*Tutte le categorie*/}
        <SiteLink to={`/${blogName}/${CATEGORIES_MAP[CATEGORY_ENUM.MOBILITA_ELETTRICA].categoryPath}/`} internal name={CATEGORIES_MAP[CATEGORY_ENUM.MOBILITA_ELETTRICA].label}>
          <Category size={CATEGORY_SIZES.large} category={CATEGORY_ENUM.MOBILITA_ELETTRICA}
                    css={[Spacing.Margin.top_48, Spacing.Margin.bottom_20, Colors.Color.eerieBlack800]}/>
        </SiteLink>
        <Slider>
          {eMobility.map((art, idx) => <ArticleCard key={idx} article={art}/>)}
        </Slider>
        <SiteLink to={`/${blogName}/${CATEGORIES_MAP[CATEGORY_ENUM.LUCE_GAS].categoryPath}/`} internal name={CATEGORIES_MAP[CATEGORY_ENUM.LUCE_GAS].label}>
          <Category size={CATEGORY_SIZES.large} category={CATEGORY_ENUM.LUCE_GAS}
                    css={[Spacing.Margin.bottom_20, Spacing.Margin.top_48, Colors.Color.eerieBlack800]}/>
        </SiteLink>
        <Slider>
          {luceGas.map((art, idx) => <ArticleCard key={idx} article={art}/>)}
        </Slider>
        <SiteLink to={`/${blogName}/${CATEGORIES_MAP[CATEGORY_ENUM.FOTOVOLTAICO].categoryPath}/`} internal name={CATEGORIES_MAP[CATEGORY_ENUM.FOTOVOLTAICO].label}>
          <Category size={CATEGORY_SIZES.large} category={CATEGORY_ENUM.FOTOVOLTAICO}
                    css={[Spacing.Margin.bottom_20, Spacing.Margin.top_48, Colors.Color.eerieBlack800]}/>
        </SiteLink>
        <Slider>
          {fotovoltaico.map((art, idx) => <ArticleCard key={idx} article={art}/>)}
        </Slider>

        <PageBreak/>

        {/*Categorie*/}
        <div css={[Typography.heading.h5_700, Spacing.Margin.top_48, Spacing.Margin.bottom_20]}>Categorie</div>
        <Slider>
          <CategoryCard type={CATEGORY_ENUM.LUCE_GAS}/>
          <CategoryCard type={CATEGORY_ENUM.MOBILITA_ELETTRICA}/>
          <CategoryCard type={CATEGORY_ENUM.FOTOVOLTAICO}/>
        </Slider>
      </Section>
    </Body>
  )
}
export default BlogHomepage
export const GHOST_POST_FRAGMENT = graphql`
  fragment GhostPostFragment on GhostPost {
        uuid
        slug
        title
        tags{
          slug
        }
        published_at
        feature_image
        feature_image_sharp{
          childImageSharp {
            gatsbyImageData(height: 202)
          }
        }
  }
`

export const postQuery = graphql`
  query($mostViewed: [String]) {
    recentsPosts: allGhostPost(
      sort: { order: DESC, fields: published_at }
      limit: 10
    ) {
      nodes {
        ...GhostPostFragment
      }
    }
    mostViewed: allGhostPost(
      limit: 9
      filter: { uuid: {  in: $mostViewed } }
    ) {
      nodes {
        ...GhostPostFragment
      }
    }
    luceGasPosts: allGhostPost(
      sort: { order: DESC, fields: published_at }
      limit: 9
      filter: { primary_tag: { name: {}, slug: { eq: "categoria_luce-e-gas" } } }
    ) {
      nodes {
        ...GhostPostFragment
      }
    }
    mobilitaElettricaPosts: allGhostPost(
      sort: { order: DESC, fields: published_at }
      limit: 9
      filter: { primary_tag: { name: {}, slug: { eq: "categoria_mobilita-elettrica" } } }
    ) {
      nodes {
        ...GhostPostFragment
      }
    }
    fotovoltaicoPosts: allGhostPost(
      sort: { order: DESC, fields: published_at }
      limit: 9
      filter: { primary_tag: { name: {}, slug: { eq: "categoria_fotovoltaico" } } }
    ) {
      nodes {
        ...GhostPostFragment
      }
    }
  }
`
